
import React from "react";
import { Interview } from "@/types/interview";
import { AttentionProvider } from "@/components/attention/AttentionContext";
import InterviewHeader from "../InterviewHeader";
import InterviewActions from "./InterviewActions";
import InterviewContainer from "./InterviewContainer";
import ContentLayout from "./ContentLayout";
import ExpandButton from "./ExpandButton";
import ExpandedContent from "./ExpandedContent";
import { useInterviewCard } from "@/hooks/interview/useInterviewCard";
import { useAttention } from "@/components/attention/AttentionContext";

interface InterviewCardProps extends Interview {
  isReversed?: boolean;
  articleNumber?: number;
}

const InterviewCardContent = (props: InterviewCardProps) => {
  const {
    id,
    question,
    answer,
    intervieweeName,
    title,
    jobTitle,
    company,
    context,
    contextUrl,
    contextImageUrl,
    contextImageCaption,
    tags,
    isReversed = false,
    aiSummary,
    aiResponse,
    articleNumber,
    isBookmarked = false,
    isLiked = false,
    summary_status = 'completed',
    summary_error,
    photoUrl,
    photoUrl2,
    photographerCredit
  } = props;

  const attention = useAttention();

  const {
    isExpanded,
    setIsExpanded,
    aiChat,
    handleAiQuestion,
    handleAiResponse,
    wordCount,
    renderKey,
    validTimestamp,
    readingTime,
    audioDuration,
    processedSummary
  } = useInterviewCard(props);

  // Track expand click
  const handleExpandClick = () => {
    if (!isExpanded && attention) {
      attention.trackClick('expand');
    }
    setIsExpanded(!isExpanded);
  };

  return (
    <InterviewContainer isReversed={isReversed} intervieweeName={intervieweeName}>
      <InterviewHeader 
        timestamp={validTimestamp}
        answer={answer}
        aiSummary={processedSummary}
        aiResponse={aiResponse}
        articleNumber={articleNumber || 0}
        wordCount={articleNumber === 1 ? wordCount : undefined}
        isLiked={isLiked}
        isBookmarked={isBookmarked}
        context={context}
        question={question}
        intervieweeName={intervieweeName}
        title={title}
        company={company}
        readingTime={readingTime}
        audioDuration={audioDuration}
        id={id}
        isSponsored={props.isSponsored}
      />

      <InterviewActions />

      <ContentLayout 
        isReversed={isReversed}
        context={context}
        contextUrl={contextUrl}
        contextImageUrl={contextImageUrl}
        contextImageCaption={contextImageCaption}
        question={question}
        answer={answer}
        intervieweeName={intervieweeName}
        title={title}
        jobTitle={jobTitle}
        company={company}
        tags={tags}
        isExpanded={isExpanded}
        photoUrl={photoUrl}
        photoUrl2={photoUrl2}
        sponsorLogoUrl={props.sponsorLogoUrl}
        sponsorLogoPosition={props.sponsorLogoPosition}
        interviewId={id}
        sourceArticles={props.sourceArticles}
        photographerCredit={photographerCredit}
        articleType={props.articleType}
        isSponsored={props.isSponsored}
        key={`content-${renderKey}`}
      />
      
      <ExpandedContent
        isExpanded={isExpanded}
        aiSummary={processedSummary}
        aiResponse={aiResponse}
        aiChat={aiChat}
        onAiQuestion={handleAiQuestion}
        onAiResponse={handleAiResponse}
        summary_status={summary_status}
        summary_error={summary_error}
        context={context}
        question={question}
        answer={answer}
        intervieweeName={intervieweeName}
        title={title}
        company={company}
        interviewId={props.id}
        tags={tags}
        sourceArticles={props.sourceArticles}
      />
      
      <ExpandButton
        isExpanded={isExpanded}
        onClick={handleExpandClick}
      />
    </InterviewContainer>
  );
};

// Wrapper component that provides AttentionProvider context
const InterviewCard = (props: InterviewCardProps) => {
  return (
    <AttentionProvider interviewId={props.id} enabled={true}>
      <InterviewCardContent {...props} />
    </AttentionProvider>
  );
};

export default InterviewCard;
